import React, { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/Modal";
import { Heart, Volume2 } from "lucide-react";

const BELLS = ["RECESS!", "PERIOD OVER!", "SCHOOL IS OVER!"];

const CHALK_SUGGESTIONS = ["GOOD MORNING STUDENTS", "TODAY'S HOMEWORK", "CLASS TEST — PAGE 48", "SILENCE!", "WHO IS TALKING?"];

export const BlackboardModal = ({ open, onClose, unlocked, onAdd, itemId, value = "", onChange }) => {
  const set = (v) => onChange && onChange(v);
  useEffect(() => { if (open && itemId && !unlocked) onAdd(itemId); }, [open, itemId]); // eslint-disable-line
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} testId="blackboard-modal" maxWidth="max-w-lg">
      <div className="p-5 sm:p-7">
        <h2 className="font-signboard text-2xl font-bold text-kirana-brown">The Blackboard</h2>
        <p className="text-xs text-kirana-brown/70">Pick up the chalk and write — it stays on the class board.</p>
        <div className="relative mt-3 flex h-52 items-center justify-center rounded-md border-[6px] border-[#6E4A28] bg-[#20361F] p-4">
          <span className="font-signboard whitespace-pre-wrap break-words text-center text-3xl leading-tight text-white/90 drop-shadow" style={{ textShadow: "0 0 6px rgba(255,255,255,0.25)" }}>
            {value || "…"}
          </span>
          <span className="absolute bottom-2 right-3 h-2 w-12 rounded bg-[#C9A66B]" />
        </div>

        <input
          data-testid="blackboard-input"
          value={value}
          maxLength={80}
          onChange={(e) => set(e.target.value)}
          placeholder="Write on the board…"
          className="mt-3 w-full rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-2 text-sm text-kirana-brown outline-none placeholder:text-kirana-brown/40"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {CHALK_SUGGESTIONS.map((s) => (
            <button key={s} data-testid={`chalk-chip-${s.slice(0, 6)}`} onClick={() => set(s)}
              className="rounded-full border-2 border-kirana-brown bg-kirana-gold px-2.5 py-1 text-[11px] font-bold text-kirana-brown transition-transform hover:scale-105">
              {s}
            </button>
          ))}
          <button data-testid="blackboard-clear" onClick={() => set("")}
            className="rounded-full border-2 border-kirana-brown bg-kirana-cream px-2.5 py-1 text-[11px] font-bold text-kirana-brown hover:bg-kirana-red hover:text-white">
            Duster (clear)
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs italic text-kirana-brown/60">Whatever you write shows on the classroom board behind you.</span>
          <span className="flex items-center gap-1 text-sm font-bold text-kirana-red"><Heart className="h-4 w-4 fill-kirana-red" /> Memory unlocked</span>
        </div>
      </div>
    </Modal>
  );
};

const BELL_SRC = { old: "/audio/bell_old.mp3", new: "/audio/bell_new.mp3" };

export const BellModal = ({ open, onClose, onAdd, itemId, unlocked }) => {
  const [msg, setMsg] = useState(BELLS[0]);
  const [choice, setChoice] = useState("new");
  const audioRef = useRef(null);

  const ring = (which) => {
    const a = audioRef.current;
    if (!a) return;
    const src = BELL_SRC[which || choice];
    if (!a.src.endsWith(src)) a.src = src;
    a.currentTime = 0;
    a.play().catch(() => {});
  };

  useEffect(() => {
    if (open) {
      setMsg(BELLS[Math.floor(Math.random() * BELLS.length)]);
      ring(choice);
      if (itemId && !unlocked) onAdd(itemId);
    } else {
      const a = audioRef.current;
      if (a) { a.pause(); a.currentTime = 0; }
    }
    return () => { const a = audioRef.current; if (a) { a.pause(); } };
  }, [open]); // eslint-disable-line

  const pick = (which) => { setChoice(which); ring(which); };

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} testId="bell-modal" maxWidth="max-w-md">
      <audio ref={audioRef} preload="auto" />
      <div className="p-6 text-center">
        <h2 className="font-signboard text-2xl font-bold text-kirana-brown">🔔 School Bell</h2>
        <p className="mt-3 font-signboard text-3xl font-bold text-kirana-red animate-heart-pop">TRRRRIIIIINNNGGGG!</p>
        <p className="mt-3 inline-block rounded-md border-[3px] border-kirana-brown bg-kirana-gold px-5 py-2 font-signboard text-2xl font-bold text-kirana-brown hard-shadow">{msg}</p>
        <p className="mt-4 text-sm italic text-kirana-brown/70">Bags zipping, benches scraping, everyone rushing out…</p>

        <div className="mt-5 flex items-center justify-center gap-2">
          <button data-testid="bell-choice-old" onClick={() => pick("old")}
            className={`rounded-md border-[3px] border-kirana-brown px-4 py-2 text-sm font-bold transition-transform hover:scale-105 ${choice === "old" ? "bg-kirana-brown text-kirana-cream" : "bg-kirana-cream text-kirana-brown"}`}>
            🔔 Old Bell
          </button>
          <button data-testid="bell-choice-new" onClick={() => pick("new")}
            className={`rounded-md border-[3px] border-kirana-brown px-4 py-2 text-sm font-bold transition-transform hover:scale-105 ${choice === "new" ? "bg-kirana-brown text-kirana-cream" : "bg-kirana-cream text-kirana-brown"}`}>
            🛎️ New Bell
          </button>
        </div>
        <button data-testid="bell-ring-again" onClick={() => ring(choice)}
          className="mt-4 inline-flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-red px-5 py-2.5 font-bold text-white transition-transform hover:scale-[1.04] hard-shadow">
          <Volume2 className="h-5 w-5" /> Ring the {choice} bell
        </button>
      </div>
    </Modal>
  );
};
